import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';

// Context Providers
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';

// Layout Components
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ChatWidget } from './components/ChatWidget';

// Pages
import { HomePage } from './pages/HomePage';
import { ProductsPage } from './pages/ProductsPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { CartPage } from './pages/CartPage';
import { CheckoutPage, CheckoutSuccessPage, CheckoutCancelPage } from './pages/CheckoutPage';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import { CustomDesignPage } from './pages/CustomDesignPage';
import { QuotePage } from './pages/QuotePage';
import { DashboardPage, OrdersPage, OrderDetailPage, ProjectsPage } from './pages/DashboardPages';
import { AdminPage } from './pages/AdminPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { TemplateEditorPage } from './pages/TemplateEditorPage';
import { PartnerPage } from './pages/PartnerPage';
import PartnerPortalPage from './pages/PartnerPortalPage';
import WishlistPage from './pages/WishlistPage';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Layout wrapper
function Layout({ children }) {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <ChatWidget />
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      <Route path="/products" element={<Layout><ProductsPage /></Layout>} />
      <Route path="/products/:id" element={<Layout><ProductDetailPage /></Layout>} />
      <Route path="/custom-design" element={<Layout><CustomDesignPage /></Layout>} />
      <Route path="/quote" element={<Layout><QuotePage /></Layout>} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<Layout><LoginPage /></Layout>} />
      <Route path="/register" element={<Layout><RegisterPage /></Layout>} />
      
      {/* Cart & Checkout */}
      <Route path="/cart" element={<Layout><CartPage /></Layout>} />
      <Route path="/checkout" element={<Layout><ProtectedRoute><CheckoutPage /></ProtectedRoute></Layout>} />
      <Route path="/checkout/success" element={<Layout><ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute></Layout>} />
      <Route path="/checkout/cancel" element={<Layout><CheckoutCancelPage /></Layout>} />
      
      {/* Dashboard Routes */}
      <Route path="/dashboard" element={<Layout><ProtectedRoute><DashboardPage /></ProtectedRoute></Layout>} />
      <Route path="/dashboard/orders" element={<Layout><ProtectedRoute><OrdersPage /></ProtectedRoute></Layout>} />
      <Route path="/dashboard/orders/:id" element={<Layout><ProtectedRoute><OrderDetailPage /></ProtectedRoute></Layout>} />
      <Route path="/dashboard/projects" element={<Layout><ProtectedRoute><ProjectsPage /></ProtectedRoute></Layout>} />
      <Route path="/dashboard/projects/:id" element={<Layout><ProtectedRoute><ProjectsPage /></ProtectedRoute></Layout>} />
      
      {/* Admin */}
      <Route path="/admin" element={<Layout><ProtectedRoute><AdminPage /></ProtectedRoute></Layout>} />
      
      {/* Templates */}
      <Route path="/templates" element={<Layout><TemplatesPage /></Layout>} />
      <Route path="/templates/:id/edit" element={<Layout><ProtectedRoute><TemplateEditorPage /></ProtectedRoute></Layout>} />
      
      {/* Partner */}
      <Route path="/partners" element={<Layout><PartnerPage /></Layout>} />
      <Route path="/partner-portal" element={<Layout><ProtectedRoute><PartnerPortalPage /></ProtectedRoute></Layout>} />
      <Route path="/wishlist" element={<Layout><ProtectedRoute><WishlistPage /></ProtectedRoute></Layout>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppRoutes />
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            toastOptions={{
              style: {
                fontFamily: 'Manrope, sans-serif',
              },
            }}
          />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
